import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  CAlert, CButton, CCard, CCardBody, CDropdown, CDropdownDivider, CDropdownItem, CDropdownMenu, CDropdownToggle,
  CFormTextarea, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle,
} from '@coreui/react';
import { api, problemMessage, type Schemas } from '../../api/client';
import { useMe } from '../../auth/AuthGate';
import { startTourOnce } from '../../tour/tour';

type Contract = Schemas['ContractResponse'];
type Operation = Schemas['AvailableOperationResponse'];

/**
 * Lišta nad detailem: kdo je na tahu, co má udělat — a tlačítko, kterým to udělá.
 * <p>
 * Vedle je jen hlavní krok. Vrátit, zrušit a smazat jsou v menu „Další akce": dělají se
 * zřídka a nováček je nemá mít na očích vedle toho, co se čeká. Frontend nezná jediné
 * pravidlo flow — vykreslí, co vrátí `GET /operations`, a u zakázaného kroku řekne proč.
 */
export function NextStepBar({ contract, version, onChanged }: {
  contract: Contract; version: number; onChanged: (c: Contract) => void;
}) {
  const [operations, setOperations] = useState<Operation[] | null>(null);
  const [step, setStep] = useState<Schemas['NextStepResponse'] | null>(null);
  const [pending, setPending] = useState<Operation | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Po kazde zmene na zakazce se muze zmenit, co jde udelat - i po zmene, ktera rowVersion
  // nezvedne (prirazeni sestavitele odemyka "Schvalit k vyrobe"). Proto verze z detailu.
  const load = useCallback(() => {
    api.GET('/api/v1/contracts/{contractId}/operations', { params: { path: { contractId: contract.id } } })
      .then(({ data }) => setOperations(data ?? []));
    api.GET('/api/v1/contracts/{id}/next-step', { params: { path: { id: contract.id } } })
      .then(({ data }) => setStep(data ?? null));
  }, [contract.id]);

  useEffect(load, [load, version]);

  // Prvni otevreni zakazky - az je lista vykreslena, jinak by pruvodce nemel na co ukazat.
  const me = useMe();
  const loaded = operations !== null;
  // Na cvicne zakazce vede cloveka PracticeCoach - dva pruvodci naraz by se prekryvali.
  useEffect(() => { if (loaded && !contract.isPractice) startTourOnce('detail', me.memberId); }, [loaded, me.memberId, contract.isPractice]);

  async function perform(operation: Operation, withComment: string | null) {
    setBusy(true);
    setError(null);

    const { data, error: problem } = await api.POST('/api/v1/contracts/{contractId}/operations', {
      params: { path: { contractId: contract.id } },
      body: { operationCode: operation.code, comment: withComment, expectedRowVersion: contract.rowVersion },
    });

    setBusy(false);

    if (data) {
      setPending(null);
      setComment('');
      onChanged(data.contract);
    } else {
      setError(problemMessage(problem));
    }
  }

  function start(operation: Operation) {
    if (operation.requiresComment || operation.buttonStyle === 'danger') {
      setError(null);
      setPending(operation);
    } else {
      void perform(operation, null);
    }
  }

  if (!operations) return null;

  // Hlavni krok = primarni operace. Kdyz zadna neni (zrusena zakazka), vytahnou se na
  // listu vedlejsi - jinak by "Obnovit" bylo schovane v menu vedle prazdne listy.
  const primary = operations.filter((o) => o.buttonStyle === 'primary');
  const shown = primary.length > 0 ? primary : operations.filter((o) => o.buttonStyle === 'secondary');
  const more = operations.filter((o) => !shown.includes(o));
  // Duvody, proc hlavni krok nejde, patri jen tomu, kdo je na tahu - kdyz se ceka na
  // nekoho jineho, veta "Čeká se na…" rika vsechno a seznam by to jen opakoval.
  const blockedReasons = step && !step.isMine
    ? []
    : [...new Set(shown.filter((o) => !o.enabled).flatMap((o) => o.blockingReasons.map((r) => r.message)))];

  return (
    <CCard className={`mb-4 ${step?.isMine ? 'st-my-turn' : ''}`} data-tour="next-step">
      <CCardBody className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
        <div>
          {step ? (step.isMine
            ? <div><strong>Na tahu jste vy.</strong></div>
            : <div>Čeká se na: <strong>{step.waitingOn}</strong> — {step.action.toLowerCase()}</div>)
            : <div className="text-body-secondary">Se zakázkou se už nic dalšího nedělá.</div>}
          {blockedReasons.length > 0 && (
            <ul className="small text-body-secondary mb-0 mt-1 ps-3">
              {blockedReasons.map((r) => <li key={r}>{r}</li>)}
            </ul>
          )}
          {error && !pending && <div className="small text-danger mt-1">{error}</div>}
        </div>

        <div className="d-flex gap-2 align-items-center">
          {shown.map((operation) => (
            <CButton key={operation.code} color="primary" variant={operation.buttonStyle === 'primary' ? undefined : 'outline'}
              data-tour={operation.enabled && operation.buttonStyle === 'primary' ? 'next-step-button' : undefined}
              disabled={!operation.enabled || busy} onClick={() => start(operation)}>
              {operation.name}
            </CButton>
          ))}
          <CDropdown alignment="end">
            <CDropdownToggle color="secondary" variant="ghost" data-tour="more-actions">Další akce</CDropdownToggle>
            <CDropdownMenu>
              {more.map((operation) => (
                <CDropdownItem key={operation.code} as="button" disabled={!operation.enabled}
                  className={operation.buttonStyle === 'danger' ? 'text-danger' : undefined}
                  onClick={() => start(operation)}>
                  {operation.name}…
                </CDropdownItem>
              ))}
              {more.length > 0 && <CDropdownDivider />}
              <CDropdownItem as="button" className="text-body-secondary" onClick={() => setDeleting(true)}>
                Smazat — založeno omylem…
              </CDropdownItem>
            </CDropdownMenu>
          </CDropdown>
        </div>
      </CCardBody>

      <CModal visible={pending !== null} onClose={() => setPending(null)}>
        <CModalHeader><CModalTitle>{pending?.name}</CModalTitle></CModalHeader>
        <CModalBody>
          <CFormTextarea
            rows={3}
            label={pending?.requiresComment ? 'Proč (povinné)' : 'Proč'}
            placeholder="Uvidí to ostatní v historii zakázky."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {error && <CAlert color="danger" className="mt-3 mb-0">{error}</CAlert>}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="ghost" onClick={() => setPending(null)}>Zpět</CButton>
          <CButton
            color={pending?.buttonStyle === 'danger' ? 'danger' : 'primary'}
            disabled={busy || (pending?.requiresComment === true && !comment.trim())}
            onClick={() => pending && void perform(pending, comment.trim() || null)}
          >
            {pending?.name}
          </CButton>
        </CModalFooter>
      </CModal>

      {deleting && <DeleteContract contract={contract} onClose={() => setDeleting(false)} />}
    </CCard>
  );
}

/** Smazání omylem založené zakázky. S povinným důvodem — není to „zrušit" (ADR-016). */
function DeleteContract({ contract, onClose }: { contract: Contract; onClose: () => void }) {
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function remove() {
    const { error: problem, response } = await api.DELETE('/api/v1/contracts/{id}', {
      params: { path: { id: contract.id }, query: { reason } },
    });

    if (response.ok) {
      navigate('/zakazky');
    } else {
      setError(problemMessage(problem));
    }
  }

  return (
    <CModal visible onClose={onClose}>
      <CModalHeader><CModalTitle>Smazat zakázku {contract.contractNumber}</CModalTitle></CModalHeader>
      <CModalBody>
        <p>
          Jen pro zakázku, která <strong>neměla vzniknout</strong> — překlep, duplicita, zkouška.
          Když se zakázka jen dělat nebude, použijte <em>Zrušit zakázku</em> — ta zůstane v archivu.
        </p>
        <CFormTextarea rows={2} label="Důvod (povinný)" value={reason} onChange={(e) => setReason(e.target.value)} />
        {error && <CAlert color="danger" className="mt-3 mb-0">{error}</CAlert>}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="ghost" onClick={onClose}>Zpět</CButton>
        <CButton color="danger" disabled={!reason.trim()} onClick={() => void remove()}>Smazat</CButton>
      </CModalFooter>
    </CModal>
  );
}
